import * as React from 'react';
import { Box, Button, Fade, IconButton, ListSubheader, Menu, MenuItem, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { MdDashboardCustomize } from 'react-icons/md';

import { catalogDropdownMenuSlotProps } from './catalogDropdownMenuProps';

export type OdooTopNavLink = { to: string; label: string };

/**
 * One top-bar entry: either a direct link button or a labelled dropdown of links
 * (same two shapes the hand-written module bars already use).
 */
export type OdooTopNavEntry = {
    key: string;
    label: string;
    to?: string;
    items?: OdooTopNavLink[];
};

/** Matches the `gap: 0.25` (2px) the module bars use between nav buttons. */
const GAP_PX = 2;

const rowSx = {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: `${GAP_PX}px`,
    overflow: 'hidden',
    py: 0.5,
    '& > *': { flexShrink: 0 },
} as const;

const navBtnSx = { whiteSpace: 'nowrap' as const, fontWeight: 500 };

const subheaderSx = {
    fontWeight: 700,
    fontSize: 13,
    color: 'primary.main',
    lineHeight: 1.3,
    py: 0.75,
    pl: 1.25,
    pr: 2,
} as const;

const nestedItemSx = { pl: 3.5, py: 1, fontSize: 13 } as const;

/**
 * Horizontal nav row that keeps every destination reachable at any width: entries that
 * no longer fit move into an overflow dropdown behind a single icon button (Odoo's "+"
 * overflow menu, with a distinct icon), instead of being clipped or scrolled off-screen.
 */
export function OdooTopNavOverflowRow(props: { entries: OdooTopNavEntry[]; sx?: SxProps<Theme> }) {
    const { entries } = props;
    const translate = useTranslate();
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const measureRef = React.useRef<HTMLDivElement | null>(null);
    const [visibleCount, setVisibleCount] = React.useState(entries.length);

    // Re-measure whenever the set of entries changes (permissions resolving, locale switch).
    const entriesSignature = entries.map(e => `${e.key}:${e.label}`).join('|');

    React.useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const measureFit = () => {
            const available = container.clientWidth;
            const probes = Array.from(measure.children) as HTMLElement[];
            // Last probe is the overflow button itself; the rest mirror the nav entries.
            if (available <= 0 || probes.length < 2) return;
            const moreWidth = probes[probes.length - 1].getBoundingClientRect().width;
            const widths = probes.slice(0, -1).map(el => el.getBoundingClientRect().width);
            const totalWidth = widths.reduce((sum, w) => sum + w + GAP_PX, 0);

            let next = widths.length;
            if (totalWidth > available) {
                let used = moreWidth + GAP_PX;
                next = 0;
                for (const width of widths) {
                    if (used + width + GAP_PX > available) break;
                    used += width + GAP_PX;
                    next += 1;
                }
            }
            setVisibleCount(prev => (prev === next ? prev : next));
        };

        measureFit();
        const observer = new ResizeObserver(measureFit);
        observer.observe(container);
        // Button widths also change when webfonts finish loading — the probe row resizes then.
        observer.observe(measure);
        return () => observer.disconnect();
    }, [entriesSignature]);

    const visible = entries.slice(0, visibleCount);
    const overflow = entries.slice(visibleCount);

    return (
        <Box ref={containerRef} sx={{ ...rowSx, ...props.sx } as SxProps<Theme>}>
            {/* Off-screen probe row: natural (unshrunk) widths for every entry plus the
                overflow button, so the fit calculation never depends on what is rendered. */}
            <Box
                ref={measureRef}
                aria-hidden
                sx={{
                    position: 'absolute',
                    top: -9999,
                    left: 0,
                    display: 'flex',
                    gap: `${GAP_PX}px`,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                }}
            >
                {entries.map(entry => (
                    <Button key={entry.key} color="inherit" size="small" sx={navBtnSx} tabIndex={-1}>
                        {entry.label}
                    </Button>
                ))}
                <IconButton color="inherit" size="small" tabIndex={-1}>
                    <MdDashboardCustomize size={20} />
                </IconButton>
            </Box>

            {visible.map(entry =>
                entry.items ? (
                    <NavDropdownButton key={entry.key} label={entry.label} items={entry.items} />
                ) : (
                    <Button
                        key={entry.key}
                        color="inherit"
                        size="small"
                        component={Link}
                        to={entry.to ?? ''}
                        sx={navBtnSx}
                    >
                        {entry.label}
                    </Button>
                )
            )}

            {overflow.length > 0 ? (
                <OverflowMenuButton
                    entries={overflow}
                    label={translate('shell.nav.more_menus', { _: 'More menus' })}
                />
            ) : null}
        </Box>
    );
}

function NavDropdownButton(props: { label: string; items: OdooTopNavLink[] }) {
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);

    return (
        <>
            <Button
                color="inherit"
                size="small"
                onClick={e => setAnchor(e.currentTarget)}
                sx={navBtnSx}
            >
                {props.label}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {props.items.map(item => (
                    <MenuItem
                        key={item.to}
                        component={Link}
                        to={item.to}
                        onClick={() => setAnchor(null)}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

/**
 * The overflow ("more menus") button. Plain links list flat; dropdown entries keep their
 * grouping as a subheader + indented items, same as the drawer sections.
 */
function OverflowMenuButton(props: { entries: OdooTopNavEntry[]; label: string }) {
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const close = () => setAnchor(null);

    return (
        <>
            <Tooltip title={props.label}>
                <IconButton
                    color="inherit"
                    size="small"
                    aria-label={props.label}
                    onClick={e => setAnchor(e.currentTarget)}
                >
                    <MdDashboardCustomize size={20} />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={close}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {props.entries.flatMap(entry =>
                    entry.items
                        ? [
                              <ListSubheader key={`h:${entry.key}`} disableSticky sx={subheaderSx}>
                                  {entry.label}
                              </ListSubheader>,
                              ...entry.items.map(item => (
                                  <MenuItem
                                      key={`i:${entry.key}:${item.to}`}
                                      component={Link}
                                      to={item.to}
                                      onClick={close}
                                      sx={nestedItemSx}
                                  >
                                      {item.label}
                                  </MenuItem>
                              )),
                          ]
                        : [
                              <MenuItem
                                  key={entry.key}
                                  component={Link}
                                  to={entry.to ?? ''}
                                  onClick={close}
                              >
                                  {entry.label}
                              </MenuItem>,
                          ]
                )}
            </Menu>
        </>
    );
}

export default OdooTopNavOverflowRow;
